import { useEffect, useMemo, useState } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const AUTH_API_BASE = `${API_ROOT}/api/auth`;
const PROFILE_API_BASE = API_ROOT
  ? (API_ROOT.endsWith("/api") ? API_ROOT : `${API_ROOT}/api`)
  : "/api";
const LEGACY_PORTAL_BASE = (() => {
  const configured = String(process.env.REACT_APP_LEGACY_PORTAL_BASE || "").trim();
  const browserOrigin = typeof window !== "undefined" ? window.location.origin : "";

  if (!configured) {
    return browserOrigin;
  }

  try {
    const parsed = new URL(configured, browserOrigin || undefined);
    const isHttpsPage = typeof window !== "undefined" && window.location.protocol === "https:";
    const isLocalHost = /^(127\.0\.0\.1|localhost)$/i.test(parsed.hostname);

    // Prevent mixed-content in production and avoid localhost/IP URLs on deployed HTTPS pages.
    if (isHttpsPage && (parsed.protocol === "http:" || isLocalHost)) {
      return browserOrigin.replace(/\/$/, "");
    }

    return parsed.toString().replace(/\/$/, "");
  } catch {
    return browserOrigin.replace(/\/$/, "");
  }
})();
const ALLOWED_PROFILE_MODULES = [1, 3, 7, 13, 15, 16, 17, 19, 21];
const EMPLOYEE_DETAILED_MODULES = [1, 3, 7, 13, 16, 19];

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);
  const isAllowed = ALLOWED_PROFILE_MODULES.includes(moduleId);

  useEffect(() => {
    if (!localUser?.id || Number.isNaN(moduleId)) {
      setError("Missing user session. Please login again.");
      setLoading(false);
      return;
    }

    if (!isAllowed) {
      setError("Unauthorized access Or Session Timed out.");
      setLoading(false);
      return;
    }

    fetch(`${AUTH_API_BASE}/profile/${localUser.id}?moduleId=${moduleId}&deptId=${localUser?.deptid || ""}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch profile");
        }
        return data;
      })
      .then((data) => setProfile(data))
      .catch((err) => setError(err.message || "Failed to fetch profile"))
      .finally(() => setLoading(false));
  }, [isAllowed, localUser?.id, localUser?.deptid, moduleId]);

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (loading) return <div>Loading...</div>;
  if (!profile) return null;

  const formatDate = (value, shortYear = false) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yy = String(date.getFullYear()).slice(-2);
    const yyyy = date.getFullYear();
    return shortYear ? `${dd}/${mm}/${yy}` : `${dd}/${mm}/${yyyy}`;
  };

  const empTypeMap = { T: "Teaching", N: "Non-Teaching", A: "Academic Staff" };
  const jobTypeMap = {
    P: "Permanent",
    T: "Temporary Basis",
    C: "Contractual",
    D: "Daily Wages",
    M: "MTS",
    O: "Out Sourced",
    G: "Guest",
    V: "Visiting",
  };
  const statusMap = { W: "Working", E: "Extended Service", R: "Retiree", H: "Emeritus" };

  const docExt = (profile.SupportingDoc || "").split(".").pop().toLowerCase();
  const profileExt = (profile.ProfilePic || "").split(".").pop().toLowerCase();
  const employeeSupportUrl = profile.SupportingDoc ? `${PROFILE_API_BASE}/profile/supportingdoc/${encodeURIComponent(profile.SupportingDoc)}` : "";
  const employeeProfileUrl = profile.ProfilePic
    ? `${PROFILE_API_BASE}/profile/image/${encodeURIComponent(profile.ProfilePic)}`
    : "";
  const teacherSupportUrl = profile.SupportingDoc ? `${LEGACY_PORTAL_BASE}/${profile.SupportingDoc}` : "";
  const teacherProfileUrl = profile.ProfilePic ? `${LEGACY_PORTAL_BASE}/${profile.ProfilePic}` : "";

  const isEmployeeDetailed = EMPLOYEE_DETAILED_MODULES.includes(moduleId);
  const isDepartmentHead = moduleId === 15;
  const isFellow = moduleId === 17;
  const isCollegeTeacher = moduleId === 21;
  const showProfileActions = [1, 3, 7, 13, 16, 19].includes(moduleId);
  const backendBase = API_ROOT || `${window.location.protocol}//${window.location.hostname}:5000`;
  const printProfileUrl = `${backendBase}/api/profile/print/${localUser?.id}?moduleId=${moduleId}`;

  return (
    <div className="container-fluid" style={{ textAlign: "left", marginTop: "10px" }}>
      {showProfileActions && (
        <div className="d-flex flex-wrap gap-2 mb-2">
          <a href="/profile/edit" className="btn btn-primary">
            <i className="bi bi-pencil-square me-1"></i>Edit Profile
          </a>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => window.open(printProfileUrl, "_blank", "noopener,noreferrer")}
          >
            Print Profile
          </button>
        </div>
      )}
      <h3 className="ctitle">Personal Details</h3>

      <div className="col-lg-10">
        {isEmployeeDetailed && (
          <div className="table-responsive">
          <table border="1" width="100%" cellPadding="6" style={{ tableLayout: "fixed", borderCollapse: "collapse", lineHeight: "1.35", minWidth: "620px", wordBreak: "break-word", overflowWrap: "break-word" }}>
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "15.5%" }} />
              <col style={{ width: "15.5%" }} />
              <col style={{ width: "15.5%" }} />
              <col style={{ width: "15.5%" }} />
            </colgroup>
            <tbody>
              <tr>
                <td><b>Name :</b></td>
                <td>{profile.FullName || "-"}</td>
                <td rowSpan="8" colSpan="2" style={{ textAlign: "center", verticalAlign: "middle", border: "1px solid black" }}>
                  <b>Supporting Doc</b><br />
                  {/* Debug info removed */}
                  {!profile.SupportingDoc ? (
                    <b>No Supporting Document Available</b>
                  ) : ["jpg", "jpeg", "png", "gif"].includes(docExt) ? (
                    <img src={employeeSupportUrl} alt="Supporting Doc" style={{ width: "100%", maxWidth: "300px", height: "auto", maxHeight: "300px", borderRadius: "10px" }} />
                  ) : docExt === "pdf" ? (
                    <iframe title="Supporting Doc" src={employeeSupportUrl} width="100%" height="300px" style={{ border: "1px solid #ccc", borderRadius: "10px", maxWidth: "300px" }} />
                  ) : (
                    <b>Unsupported file format</b>
                  )}
                </td>
                <td rowSpan="8" colSpan="2" style={{ textAlign: "center", verticalAlign: "middle", border: "1px solid black" }}>
                  <b>Profile Pic</b><br />
                  {/* Debug info removed */}
                  {!profile.ProfilePic ? (
                    <b>No Profile Picture Available</b>
                  ) : ["jpg", "jpeg", "png", "gif"].includes(profileExt) ? (
                    <img src={employeeProfileUrl} alt="Profile Pic" style={{ width: "100%", maxWidth: "300px", height: "auto", maxHeight: "300px", borderRadius: "10px" }} />
                  ) : (
                    <b>Invalid profile picture format.</b>
                  )}
                </td>
              </tr>
              <tr><td><b>Father's Name</b></td><td>{profile.FullFatherName || "-"}</td></tr>
              <tr><td><b>Mothers's Name</b></td><td>{profile.FullMotherName || "-"}</td></tr>
              <tr><td><b>Gender</b></td><td>{profile.Gender || "-"}</td></tr>
              <tr><td><b>Designation</b></td><td>{profile.Designation || "-"}</td></tr>
              <tr><td><b>DOB</b></td><td>{formatDate(profile.DOB)}</td></tr>
              <tr><td><b>Valid Upto</b></td><td>{formatDate(profile.DOR)}</td></tr>
              <tr><td><b>Mobile</b></td><td>{profile.Mobile ? `+${profile.Country || ""}${profile.Mobile}` : "-"}</td></tr>

              <tr>
                <td><b>PF No./PPO No</b></td><td>{profile.PFNo || "-"}</td>
                <td><b>EMail</b></td><td>{profile.Email || "-"}</td>
                <td><b>Blood Group</b></td><td>{profile.BloodGroup || "-"}</td>
              </tr>
              <tr>
                <td><b>Joining Date</b></td><td>{formatDate(profile.DOJ)}</td>
                <td><b>Confirmation Date</b></td><td>{`${formatDate(profile.DOC)}(Present Designation)`}</td>
                <td><b>Emg Mobile</b></td><td>{profile.EmgMobile || "-"}</td>
              </tr>
              <tr>
                <td><b>Emp Type:</b></td><td>{empTypeMap[profile.EmpType] || profile.EmpType || "-"}</td>
                <td><b>Job Type :</b></td><td>{jobTypeMap[profile.JobType] || profile.JobType || "-"}</td>
                <td><b>Status :</b></td><td>{statusMap[profile.Status] || profile.Status || "-"}</td>
              </tr>
              <tr><td><b>Department</b></td><td colSpan="5">{profile.Department || "-"}</td></tr>
              <tr>
                <td><b>Residential Address</b></td>
                <td colSpan="5">{`${profile.PermanentAddress || ""} ${profile.City || ""}, ${profile.State || ""}-${profile.PermanentPIN || ""}`.trim()}</td>
              </tr>
              <tr><td colSpan="6" align="center">For Signatures</td></tr>
              <tr>
                <td>Employee</td>
                <td>Dealing Dept Clerk</td>
                <td colSpan="2">Department Head</td>
                <td colSpan="2">{profile.ResolverDesignation || "-"}</td>
              </tr>
              <tr>
                <td>{profile.FullName || "-"}</td>
                <td>{profile.DealingClerkName || "-"}</td>
                <td colSpan="2">{profile.DepartmentHead || "-"}</td>
                <td colSpan="2">{profile.ResolverName || "-"}</td>
              </tr>
              <tr>
                <td style={{ height: "35px" }}></td>
                <td></td>
                <td colSpan="2"></td>
                <td colSpan="2"></td>
              </tr>
            </tbody>
          </table>
          </div>
        )}

        {isDepartmentHead && (
          <table border="0">
            <tbody>
              <tr>
                <td colSpan="5"><font face="Lucida console" size="2"><center>{` -- Date : ${new Date().toLocaleDateString("en-GB").replace(/\//g, "-")} ****`}</center></font></td>
              </tr>
              <tr><td><b>Department :</b></td><td>{profile.Department || "-"}</td></tr>
              <tr><td><b>City :</b></td><td>{profile.City || "-"}</td></tr>
              <tr><td><b>State :</b></td><td>{profile.State || "-"}</td></tr>
            </tbody>
          </table>
        )}

        {isFellow && (
          <table border="0">
            <tbody>
              <tr>
                <td colSpan="5"><font face="Lucida console" size="2"><center>{` -- Date : ${new Date().toLocaleDateString("en-GB").replace(/\//g, "-")} ****`}</center></font></td>
              </tr>
              <tr><td><b>Name :</b></td><td>{`${profile.MemberTitle || ""} ${profile.MemberName || ""}`.trim()}</td></tr>
              <tr><td><b>Father's Name :</b></td><td>{`${profile.FNameTitle || ""} ${profile.FName || ""}`.trim()}</td></tr>
              <tr><td><b>DOB :</b></td><td>{formatDate(profile.DOB, true)}</td></tr>
              <tr><td><b>Mobile :</b></td><td>{profile.Mobile || "-"}</td></tr>
              <tr><td><b>ID Card No. :</b></td><td>{profile.IDCardNo || "-"}</td></tr>
              <tr><td><b>Type :</b></td><td>{profile.MemberType || "-"}</td></tr>
              <tr><td><b>Permanent Address :</b></td><td>{profile.PermanentAddress || "-"}</td></tr>
              <tr><td><b>City :</b></td><td>{profile.City || "-"}</td></tr>
              <tr><td><b>State :</b></td><td>{profile.State || "-"}</td></tr>
            </tbody>
          </table>
        )}

        {isCollegeTeacher && (
          <table border="0">
            <tbody>
              <tr>
                <td colSpan="5"><font face="Lucida console" size="2"><center>{` -- Date : ${new Date().toLocaleDateString("en-GB").replace(/\//g, "-")} ****`}</center></font></td>
              </tr>
              <tr>
                <td><b>Name :</b></td><td>{`${profile.TeacherTitle || ""} ${profile.TName || ""}`.trim()}</td>
                <td rowSpan="8" style={{ textAlign: "center", verticalAlign: "middle", border: "1px solid black" }}>
                  <b>Supporting Doc</b><br />
                  {!profile.SupportingDoc ? (
                    <b>No Supporting Doc Available</b>
                  ) : (
                    <img src={teacherSupportUrl} alt="Supporting Doc" style={{ width: "300px", height: "300px", borderRadius: "10px" }} />
                  )}
                </td>
                <td rowSpan="8" style={{ textAlign: "center", verticalAlign: "middle", border: "1px solid black" }}>
                  <b>Profile Pic</b><br />
                  {!profile.ProfilePic ? (
                    <b>No Profile Picture Available</b>
                  ) : (
                    <img src={teacherProfileUrl} alt="Profile Pic" style={{ width: "300px", height: "300px", borderRadius: "10px" }} />
                  )}
                </td>
              </tr>
              <tr><td><b>Father's Name :</b></td><td>{`${profile.FNameTitle || ""} ${profile.FName || ""}`.trim()}</td></tr>
              <tr><td><b>DOB :</b></td><td>{formatDate(profile.DOB, true)}</td></tr>
              {profile.ValidityDate && (
                <tr>
                  <td>
                    <b>
                      {profile.EmpType === "P" ? "Date of Retirement :" : profile.EmpType === "C" ? "Term of Appointment :" : "Validity Date :"}
                    </b>
                  </td>
                  <td>{formatDate(profile.ValidityDate, true)}</td>
                </tr>
              )}
              {profile.EmpType && (
                <tr>
                  <td><b>Employee Type :</b></td>
                  <td>{profile.EmpType === "P" ? "Permanent" : profile.EmpType === "C" ? "Contractual" : profile.EmpType}</td>
                </tr>
              )}
              <tr><td><b>Mobile :</b></td><td>{profile.Mobile || "-"}</td></tr>
              <tr><td><b>ID Card No. :</b></td><td>{profile.IDCardNo || "-"}</td></tr>
              <tr><td><b>Permanent Address :</b></td><td>{profile.PermanentAddress || "-"}</td></tr>
              <tr><td><b>City :</b></td><td>{profile.City || "-"}</td></tr>
              <tr><td><b>State :</b></td><td>{profile.State || "-"}</td></tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}